import React, { useContext, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';

import { useTrackEvent } from '@nerdwallet/features';

import { Box } from '@nerdwallet/currency/components/Box';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import { Text } from '@nerdwallet/currency/components/Text';

import { useAnalytics } from '@lib/segment';
import { useInsurancePull } from '@lib/hooks/nerdwallet-plus/use-insurance-pull';

import {
  useEmitRewardableEvent,
  useIsNerdWalletPlusMember,
} from '@lib/hooks/nerdwallet-plus';

import SegmentImpression from '@components/segment-impression';

import { Actions } from '../canopy-flow-state';
import type { Action } from '../canopy-flow-state';
import CanopyFlowContext from '../canopy-flow-context';

type FetchPoliciesScreenProps = {
  dispatch: React.Dispatch<Action>;
  pullId: string;
};

const LINKED_POLICY_METRIC_NAME = 'linked_insurance_policy';

const FetchPoliciesScreen = ({
  dispatch,
  pullId,
}: FetchPoliciesScreenProps): React.ReactNode => {
  const { segmentTrack } = useAnalytics();
  const apolloClient = useApolloClient();
  const [emitReward] = useEmitRewardableEvent();
  const { isPlatformNative } = useContext(CanopyFlowContext);

  const { isNerdWalletPlusMember, loading: nwPlusLoading } =
    useIsNerdWalletPlusMember();

  const { polling, pull, policies, policyTypeCounts } =
    useInsurancePull(pullId);

  const hasPolicies = policies.length > 0;

  const [trackExperimentMetric] = useTrackEvent();

  useEffect(
    function refreshPoliciesCache() {
      if (hasPolicies) {
        apolloClient.refetchQueries({
          updateCache(cache) {
            cache.evict({ fieldName: 'insurancePolicies' });
            cache.evict({ fieldName: 'insuranceAccounts' });
            cache.evict({ fieldName: 'insurancePolicyRateReport' });
          },
        });
      }
    },
    [apolloClient, hasPolicies],
  );

  useEffect(() => {
    if (nwPlusLoading) {
      return;
    }

    if (hasPolicies) {
      trackExperimentMetric(LINKED_POLICY_METRIC_NAME);

      policies.forEach((policy) => {
        segmentTrack('NW Plus Insurance Policy Linked', {
          policy_id: policy.id,
          policy_type: policy.policyType,
          carrier_name: policy.carrierName,
          premium: policy.totalPremiumCents / 100,
          policy_duration_months: policy.policyDurationMonths,
          is_expired: policy.isExpired,
          is_nw_plus_member: isNerdWalletPlusMember,
          is_platform_native: isPlatformNative,
        });
      });

      // For now, pick the first policy
      const firstPolicy = policies[0];
      dispatch({
        type: Actions.CHECK_POLICIES_AND_RATES,
        payload: {
          hasPolicyData: true,
          isNerdWalletPlusMember,
          isFreeRateMember: !isNerdWalletPlusMember,
          hasSeenFoundPolicies: false,
          noPoliciesFound: false,
          policy: {
            id: firstPolicy.id,
            carrierName: firstPolicy.carrierName,
            policyType: firstPolicy.policyType,
          },
          pullId,
        },
      });

      return;
    }

    if (!polling && !hasPolicies) {
      dispatch({
        type: Actions.CHECK_POLICIES_AND_RATES,
        payload: {
          hasPolicyData: false,
          isNerdWalletPlusMember,
        },
      });
    }
  }, [
    dispatch,
    emitReward,
    hasPolicies,
    isNerdWalletPlusMember,
    isPlatformNative,
    nwPlusLoading,
    policies,
    polling,
    pullId,
    segmentTrack,
    trackExperimentMetric,
  ]);

  useEffect(() => {
    if (pull?.id) {
      segmentTrack(
        'NW Plus Insurance Canopy Flow Analyzing Your Rates Pull Completed',
        {
          carrier_name: pull.carrierName,
          is_nw_plus_member: isNerdWalletPlusMember,
          is_platform_native: isPlatformNative,
        },
      );

      segmentTrack('NW Plus Insurance Carrier Linked', {
        carrier_name: pull.carrierName,
        num_policies_linked: policies.length,
        num_home_policies: policyTypeCounts?.HOMEOWNERS ?? 0,
        num_renters_policies: policyTypeCounts?.RENTERS ?? 0,
        num_auto_policies: policyTypeCounts?.AUTO ?? 0,
        is_nw_plus_member: isNerdWalletPlusMember,
        is_platform_native: isPlatformNative,
      });
    }
  }, [
    pull,
    segmentTrack,
    policies,
    policyTypeCounts,
    isNerdWalletPlusMember,
    isPlatformNative,
  ]);

  return (
    <SegmentImpression
      eventName="NW Plus Insurance Canopy Flow Analyzing Your Rates Screen Viewed"
      eventProps={{
        is_nw_plus_member: isNerdWalletPlusMember,
        is_platform_native: isPlatformNative,
      }}
    >
      <Box
        display="flex"
        alignContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Box marginBottom="2">
          <LoadingSpinner inline size={64} />
        </Box>
        <Box marginBottom="1">
          <Text align="center" component="p">
            Analyzing your rates
          </Text>
        </Box>
        <Text align="center" component="p" tone="light" size="x-small">
          Do not refresh this page
        </Text>
      </Box>
    </SegmentImpression>
  );
};

export default FetchPoliciesScreen;
